import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import type {
  NikolaAction,
  NikolaActivity,
  NikolaMessagePart,
  PlayerProfile,
  ReviewMomentReference,
} from "@chessla/shared";
import { ApiRequestError } from "@/shared/api/client";
import { useNotification } from "@/app/context/NotificationContext";
import { useAuth } from "@/app/context/AuthContext";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { fetchMyPlayerProfile } from "@/features/profile/profile.api";
import { useAnalysisStore } from "@/features/analysis/analysis.store";
import { analysisRunner } from "@/features/analysis/analysis-runner";
import { SingleGameAnalysisModal } from "@/features/analysis";
import { resolveNikolaAnalysisPreset } from "@/features/analysis/analysis-presets";
import { AnalysisProgressPanel } from "@/features/analysis/AnalysisProgressPanel";
import { claimAnalysis, fetchAnalysisGame, fetchUnanalyzedGames } from "@/features/analysis/analysis.api";
import { browserEngine, browserEngineActivity, storeBrowserEngineArtifact } from "@/features/analysis/browser-engine";
import { patternWorkspaceUrl } from "@/features/patterns";
import { withAtomicPositionProof } from "../evaluation";
import { useChatStore } from "./chat.store";
import { NIKOLA_FAILURE_REPLY } from "./chat-contracts";
import {
  createCoachingSession,
  fetchCurrentSession,
  fetchSessionMessages,
  confirmPracticeSet,
  downloadCoachExport,
  fetchChatCreditBalance,
  fetchChatCreditEstimate,
  fetchReviewChatHandoff,
  type CreditBalanceResult,
  type PendingAction,
  type SendMessageResult,
} from "./chat.api";
import {
  acquireNikolaBrowserActionLease,
  nikolaBrowserResultInput as createNikolaBrowserResultInput,
  type NikolaBrowserLeaseHandle,
  type NikolaBrowserResultInput,
} from "./nikola-run.api";
import { useNikolaRun } from "./use-nikola-run";
import {
  ChatComposer,
  NikolaChatSurface,
  NikolaComingSoon,
  ChatContextStrip,
  ChatHeader,
  ChatTurn,
  ChatWorkspaceIntro,
  currentNikolaActivityLabel,
  nikolaActivityLabel,
  NikolaActivityStatus,
  useChatViewport,
  type ChatReviewContext,
  type ChatSelectionContext,
} from "../ui";
import {
  buildChatRequestScope,
  chatDayLabel,
  chatDraftKey,
  draftAfterAcceptedSend,
  isCurrentChatRequest,
  type ChatRequestIdentity,
} from "./chat-view-state";
import { mergeNikolaActivityParts, NIKOLA_ANALYSIS_PLACEHOLDER } from "./chat-message-parts";
import { gameReviewUrl, nikolaSettingsUrl, readNikolaDraft, resolveHandoffDraft } from "./chat-handoff";
import { parseCurrentSessionMessages } from "./chat-session-messages";
import { chatResponseMessage, chatResponseMessageWithProgress } from "./chat-session-presentation";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction, resolveNikolaAction } from "@/features/entitlements/entitlement-policy";
import { useEntitlementCta } from "@/features/entitlements/use-entitlement-cta";

const requestId = (): string => crypto.randomUUID();

/**
 * Main chat screen. Position evidence stays inline with the response
 * that requested it; chat never reserves a persistent board sidebar.
 *
 * The chat store is in-memory only, so a page refresh loses sessionId —
 * on mount, if the store is empty, this tries to RESUME the player's
 * current session (GET /coach/sessions/current) rather than always
 * starting a new one, so the conversation reads as "one long chat"
 * across reloads instead of restarting every time. A session only
 * becomes non-resumable once it's been summarized (see coach.service.ts
 * — summarization happens lazily, right before the NEXT session would
 * be created), so in practice a session stays open/resumable until the
 * player explicitly starts a new one from a fresh browser context after
 * time has passed. If there's nothing to resume (brand new player, or
 * their last session already finished), falls back to creating a new
 * session exactly as before.
 */
export const ChatPage = () => {
  const { userId } = useAuth();
  if (import.meta.env.VITE_NIKOLA_DISABLED === "true") return <NikolaComingSoon />;
  return <ChatPageContent key={userId ?? "signed-out"} />;
};

const ChatPageContent = () => {
  const { messages, sessionId, appendMessage, setSession, setMessages, updateLastMessage, updateMessage } =
    useChatStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const queryGameId = searchParams.get("gameId");
  const queryFactId = searchParams.get("factId");
  const stateReviewMoment = (location.state as { reviewMoment?: ReviewMomentReference } | null)?.reviewMoment;
  const reviewMoment =
    stateReviewMoment?.gameId && stateReviewMoment.factId
      ? { gameId: stateReviewMoment.gameId, factId: stateReviewMoment.factId }
      : queryGameId && queryFactId
        ? { gameId: queryGameId, factId: queryFactId }
        : undefined;
  const reviewGameId = reviewMoment?.gameId;
  const reviewFactId = reviewMoment?.factId;
  const querySessionId = searchParams.get("sessionId");
  const forceNewChat = searchParams.get("new") === "1";
  const { userId } = useAuth();
  const contextHandoff = readNikolaDraft(location.state, userId);
  const handoffDraft = contextHandoff?.draft;
  const handoffFormat = contextHandoff?.format;
  const [draft, setDraft] = useState(handoffDraft ?? "");
  const [isSending, setIsSending] = useState(false);
  const [isResuming, setIsResuming] = useState(forceNewChat || !sessionId);
  const [verifiedSessionId, setVerifiedSessionId] = useState<string | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [accountSnapshot, setAccountSnapshot] = useState<Pick<PlayerProfile, "username" | "ratings" | "record"> | null>(
    null,
  );
  const [analyzingGameId, setAnalyzingGameId] = useState<string | null>(null);
  const [analysisModalGameId, setAnalysisModalGameId] = useState<string | null>(null);
  const analyzingActionRef = useRef<PendingAction | null>(null);
  /** Non-null while a live Stockfish search for evaluate_line is running — description shown in the loading bubble, e.g. "after 21...fxg6". */
  const [evaluatingLine, setEvaluatingLine] = useState<{ fen: string; description: string } | null>(null);
  const [creditBalance, setCreditBalance] = useState<CreditBalanceResult | null>(null);
  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [creditBlocked, setCreditBlocked] = useState(false);
  const [runFailure, setRunFailure] = useState<{ message: string; retryDraft?: string } | null>(null);
  const [reviewContext, setReviewContext] = useState<ChatReviewContext | null>(null);
  const [reviewDraft, setReviewDraft] = useState<string | null>(null);
  const [selectionContext, setSelectionContext] = useState<ChatSelectionContext | null>(null);
  const [contextDismissed, setContextDismissed] = useState(false);
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const chatGate = resolveNikolaAction(entitlementSnapshot, "chat", reviewGameId ?? selectionContext?.gameId);
  const entitlementCta = useEntitlementCta(chatGate);
  const latestMessage = messages.at(-1);
  const { transcriptProps, composerRef, newReply, resumeFollow } = useChatViewport(
    messages,
    latestMessage?.role === "assistant" &&
      latestMessage.content !== NIKOLA_ANALYSIS_PLACEHOLDER &&
      Boolean(latestMessage.content.trim()),
  );
  const activityStatusLabel =
    currentNikolaActivityLabel(messages) ||
    (isSending && messages.at(-1)?.role === "user"
      ? nikolaActivityLabel({ kind: "coach_thinking", status: "running", label: "" })
      : "");
  const sendingRef = useRef(false);
  const sendRequestRef = useRef<ChatRequestIdentity | null>(null);
  const loadRequestRef = useRef<ChatRequestIdentity | null>(null);
  const actionRequestRef = useRef<ChatRequestIdentity | null>(null);
  const recoveryRequestRef = useRef<ChatRequestIdentity | null>(null);
  const terminalRunRef = useRef<object | null>(null);
  const {
    state: nikolaRunState,
    result: latestNikolaRunResult,
    error: nikolaRunError,
    execute: executeNikolaRun,
    submit: submitNikolaRunResult,
    recover: recoverNikolaRun,
    cancel: cancelActiveNikolaRun,
    fail: failNikolaRun,
    signal: nikolaRunSignal,
  } = useNikolaRun(userId, verifiedSessionId);
  const runBusy = nikolaRunState.phase === "running" || nikolaRunState.phase === "waiting";
  const actionKeysRef = useRef(new Set<string>());
  const actionLeaseRef = useRef<NikolaBrowserLeaseHandle | null>(null);
  const draftTouchedRef = useRef(false);
  const prefillKeyRef = useRef<string | null>(null);
  const jobs = useAnalysisStore((s) => s.jobs);
  useEffect(
    () => () => {
      sendRequestRef.current = null;
      actionRequestRef.current = null;
      recoveryRequestRef.current = null;
      actionLeaseRef.current?.stop();
      actionLeaseRef.current = null;
    },
    [],
  );

  /** Tag for a placeholder bubble standing in for a live progress panel (batch analysis or a single live evaluation) — distinct from any other message so cleanup can find/remove it unambiguously. */

  const isActiveSessionRequest = (token: ChatRequestIdentity, ref: { current: ChatRequestIdentity | null }): boolean =>
    isCurrentChatRequest(token, ref.current, useChatStore.getState().sessionId ?? "");
  const refreshCredits = async (
    token?: ChatRequestIdentity,
    ref?: { current: ChatRequestIdentity | null },
  ): Promise<void> => {
    const balance = await fetchChatCreditBalance().catch(() => null);
    if (token && ref && !isActiveSessionRequest(token, ref)) return;
    if (balance) setCreditBalance(balance);
  };

  const reportNoAnswer = (message = NIKOLA_FAILURE_REPLY, retryDraft?: string): void =>
    setRunFailure({ message, ...(retryDraft ? { retryDraft } : {}) });
  const reportError = (error: unknown): void => {
    if (error instanceof ApiRequestError) {
      if (error.code === "QUOTA_EXCEEDED") setCreditBlocked(true);
      showNotification(error.message, "error");
      return;
    }
    reportNoAnswer();
  };

  useEffect(() => {
    sendRequestRef.current = null;
    actionRequestRef.current = null;
    recoveryRequestRef.current = null;
    terminalRunRef.current = null;
    actionLeaseRef.current?.stop();
    actionLeaseRef.current = null;
    actionKeysRef.current.clear();
    sendingRef.current = false;
    setIsSending(false);
    setAnalyzingGameId(null);
    setAnalysisModalGameId(null);
    setEvaluatingLine(null);
    if (sessionId && sessionId === verifiedSessionId && !isResuming) {
      const stored = localStorage.getItem(chatDraftKey(sessionId));
      if (handoffDraft) {
        setDraft((current) => {
          const next = resolveHandoffDraft(stored, current, handoffDraft, draftTouchedRef.current);
          localStorage.setItem(chatDraftKey(sessionId), next);
          return next;
        });
        return;
      }
      draftTouchedRef.current = (stored?.length ?? 0) > 0;
      setDraft(stored ?? "");
    }
  }, [sessionId, verifiedSessionId, handoffDraft, isResuming]);

  useEffect(() => {
    if (forceNewChat && handoffDraft && sessionId && !isResuming)
      void navigate(`/chat?sessionId=${encodeURIComponent(sessionId)}`, {
        replace: true,
        state: location.state as unknown,
      });
  }, [forceNewChat, handoffDraft, sessionId, isResuming, navigate, location.state]);

  useEffect(() => {
    void refreshCredits();
    void fetchChatCreditEstimate()
      .then((estimate) => setEstimatedCredits(estimate.reserved))
      .catch(() => undefined);
    // Stable API calls; refreshes after every billed turn below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Credit APIs are stable and refresh explicitly after billed turns.
  }, []);

  useEffect(() => {
    let current = true;
    setReviewContext(null);
    setReviewDraft(null);
    setContextDismissed(false);
    if (!reviewGameId || !reviewFactId)
      return () => {
        current = false;
      };
    const reference = { gameId: reviewGameId, factId: reviewFactId };
    void fetchReviewChatHandoff(reference)
      .then((handoff) => {
        if (!current) return;
        setReviewContext({
          reference,
          cohort: handoff.cohort,
          eyebrow: handoff.eyebrow,
          summary: handoff.summary,
          question: handoff.question,
          prompts: handoff.prompts,
        });
        setReviewDraft(handoff.draft);
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [reviewGameId, reviewFactId]);

  useEffect(() => {
    if (!forceNewChat || !sessionId || !reviewDraft || !reviewGameId || !reviewFactId || draftTouchedRef.current)
      return;
    const key = `${sessionId}:${reviewGameId}:${reviewFactId}`;
    if (prefillKeyRef.current === key) return;
    prefillKeyRef.current = key;
    setDraft(reviewDraft);
    localStorage.setItem(chatDraftKey(sessionId), reviewDraft);
  }, [forceNewChat, reviewDraft, reviewFactId, reviewGameId, sessionId]);

  const removeAnalysisPlaceholder = (): void => {
    setMessages(
      useChatStore
        .getState()
        .messages.filter(
          (message) => !(message.role === "assistant" && message.content === NIKOLA_ANALYSIS_PLACEHOLDER),
        ),
    );
  };

  const completeAnalysisPlaceholder = (result: SendMessageResult): void => {
    const messages = useChatStore.getState().messages;
    let index = messages.length - 1;
    while (
      index >= 0 &&
      !(messages[index]?.role === "assistant" && messages[index]?.content === NIKOLA_ANALYSIS_PLACEHOLDER)
    )
      index -= 1;
    if (index < 0) {
      appendMessage(chatResponseMessage(result));
      return;
    }
    updateMessage(index, chatResponseMessageWithProgress(result, messages[index]?.parts ?? []));
  };

  const mergeAnalysisPlaceholderActivity = (activity: NikolaActivity): void => {
    const messages = useChatStore.getState().messages;
    let index = messages.length - 1;
    while (
      index >= 0 &&
      !(messages[index]?.role === "assistant" && messages[index]?.content === NIKOLA_ANALYSIS_PLACEHOLDER)
    )
      index -= 1;
    if (index < 0) return;
    updateMessage(index, { parts: mergeNikolaActivityParts(messages[index]?.parts, activity) });
  };
  const showAnalysisActivity = (messageIndex: number, activity: NikolaActivity): void => {
    const current = useChatStore.getState().messages[messageIndex];
    if (current?.content === NIKOLA_ANALYSIS_PLACEHOLDER) {
      updateMessage(messageIndex, { pendingAction: null, parts: mergeNikolaActivityParts(current.parts, activity) });
    } else {
      updateMessage(messageIndex, { pendingAction: null });
      appendMessage({
        role: "assistant",
        content: NIKOLA_ANALYSIS_PLACEHOLDER,
        parts: [{ type: "activity", activity }],
      });
    }
  };

  const finishBrowserAction = async (token: ChatRequestIdentity, input: NikolaBrowserResultInput): Promise<void> => {
    if (!isActiveSessionRequest(token, actionRequestRef)) return;
    actionLeaseRef.current?.stop();
    try {
      const result = await submitNikolaRunResult(input, (activity) => {
        if (isActiveSessionRequest(token, actionRequestRef)) mergeAnalysisPlaceholderActivity(activity);
      });
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      terminalRunRef.current = result;
      if (result.status === "complete") {
        completeAnalysisPlaceholder({ ...result, pendingAction: null });
        await refreshCredits(token, actionRequestRef);
        if (result.pendingAction)
          queueMicrotask(
            () => void handleResponseAction(result.pendingAction!, useChatStore.getState().messages.length - 1),
          );
        return;
      }
      if (result.status === "failed") {
        removeAnalysisPlaceholder();
        if (result.failureCode !== "cancelled") {
          const previousQuestion = useChatStore
            .getState()
            .messages.slice()
            .reverse()
            .find(({ role }) => role === "user")?.content;
          if (result.retryable && previousQuestion) setDraft(previousQuestion);
          reportNoAnswer(
            result.failureMessage ?? NIKOLA_FAILURE_REPLY,
            result.retryable ? previousQuestion : undefined,
          );
        }
        await refreshCredits(token, actionRequestRef);
        return;
      }

      updateLastMessage({
        role: "assistant",
        content: NIKOLA_ANALYSIS_PLACEHOLDER,
        parts: result.parts ?? useChatStore.getState().messages.at(-1)?.parts,
        pendingAction: result.pendingAction ?? null,
      });
      if (result.pendingAction)
        queueMicrotask(
          () => void handleResponseAction(result.pendingAction!, useChatStore.getState().messages.length - 1),
        );
    } catch (error) {
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      removeAnalysisPlaceholder();
      reportError(error);
    } finally {
      actionLeaseRef.current?.stop();
      actionLeaseRef.current = null;
      if (isActiveSessionRequest(token, actionRequestRef)) {
        setEvaluatingLine(null);
        setAnalyzingGameId(null);
        setIsSending(false);
      }
    }
  };

  const nikolaBrowserResultInput = (
    action: PendingAction,
    result: Omit<Parameters<typeof createNikolaBrowserResultInput>[1], "leaseToken">,
  ): NikolaBrowserResultInput => {
    const leaseToken = actionLeaseRef.current?.leaseToken;
    if (!leaseToken) throw new Error("Nikola browser action lease is unavailable.");
    return createNikolaBrowserResultInput(action, { ...result, leaseToken });
  };

  const runBrowserEngine = async (
    token: ChatRequestIdentity,
    action: PendingAction,
    messageIndex: number,
  ): Promise<void> => {
    const request = action.request;
    if (!request) return;
    if (!isActiveSessionRequest(token, actionRequestRef)) return;
    showAnalysisActivity(messageIndex, browserEngineActivity(request));
    setEvaluatingLine({ fen: request.source.fen, description: "this position" });
    setIsSending(true);

    let input: NikolaBrowserResultInput;
    try {
      const calculated = await browserEngine.calculate(
        request,
        ({ depth, nodes, targetDepth }) => {
          if (!isActiveSessionRequest(token, actionRequestRef)) return;
          updateLastMessage({
            content: NIKOLA_ANALYSIS_PLACEHOLDER,
            parts: [
              {
                type: "activity",
                activity: browserEngineActivity(request, { depth, nodes, targetDepth }),
              },
            ],
          });
        },
        actionLeaseRef.current?.signal ?? nikolaRunSignal(),
      );
      const artifact = withAtomicPositionProof(calculated);
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      const stored = await storeBrowserEngineArtifact(artifact);
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      input = nikolaBrowserResultInput(action, { artifactId: stored.artifactId });
    } catch {
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      input = nikolaBrowserResultInput(action, { failed: true });
    }
    await finishBrowserAction(token, input);
  };

  const startAnalysisFromChat = async (
    token: ChatRequestIdentity,
    action: PendingAction,
    messageIndex: number,
  ): Promise<void> => {
    const gameId = action.gameId;
    if (!gameId) return;
    if (!isActiveSessionRequest(token, actionRequestRef)) return;
    showAnalysisActivity(messageIndex, { kind: "analysis_started", status: "running", label: "ANALYZING THIS GAME" });
    const preset = resolveNikolaAnalysisPreset(action.analysisPreset ?? "regular");
    const analysis = useAnalysisStore.getState();
    const existingJob = analysis.jobs.find((job) => job.gameId === gameId && job.preset.id === preset?.id);
    if (analysis.isRunning && existingJob && ["queued", "analyzing"].includes(existingJob.status)) {
      analyzingActionRef.current = action;
      setAnalyzingGameId(gameId);
      setIsSending(true);
      return;
    }
    const unanalyzed = await fetchUnanalyzedGames().catch(() => []);
    if (!isActiveSessionRequest(token, actionRequestRef)) return;
    let target = unanalyzed.find((game) => game.id === gameId);
    if (!target) {
      try {
        target = await fetchAnalysisGame(gameId);
      } catch {
        if (!isActiveSessionRequest(token, actionRequestRef)) return;
        setIsSending(true);
        await finishBrowserAction(token, nikolaBrowserResultInput(action, { gameId, failed: true }));
        return;
      }
    }
    if (!isActiveSessionRequest(token, actionRequestRef)) return;
    const existingDepth =
      "existingAnalysisDepth" in target && typeof target.existingAnalysisDepth === "number"
        ? target.existingAnalysisDepth
        : null;
    const alreadyAnalyzed = existingDepth != null;
    if (
      action.analysisMode !== "reanalyze" &&
      preset &&
      existingDepth != null &&
      existingDepth >= preset.depth &&
      "tacticalStatus" in target &&
      target.tacticalStatus === "verified"
    ) {
      await finishBrowserAction(token, nikolaBrowserResultInput(action, { gameId }));
      return;
    }
    const gate = resolveAnalysisAction(entitlementSnapshot, gameId, alreadyAnalyzed, target.exactTimeControl);
    if (!gate.allowed) {
      removeAnalysisPlaceholder();
      setAnalyzingGameId(null);
      setIsSending(false);
      failNikolaRun(new Error("Nikola cannot start this analysis with the current access."));
      if (gate.cta) void navigate(gate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
      return;
    }
    try {
      await claimAnalysis(gameId);
    } catch (caught) {
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      showNotification(
        caught instanceof ApiRequestError ? caught.message : "Nikola could not start this analysis.",
        "error",
      );
      setIsSending(true);
      await finishBrowserAction(token, nikolaBrowserResultInput(action, { gameId, failed: true }));
      return;
    }
    if (!isActiveSessionRequest(token, actionRequestRef)) return;
    const completion = preset ? analysisRunner.tryStart([target], preset, "single") : null;
    if (completion && isActiveSessionRequest(token, actionRequestRef)) {
      const signal = actionLeaseRef.current?.signal;
      const stopOwnedAnalysis = () => {
        const job = useAnalysisStore.getState().jobs.find((candidate) => candidate.gameId === gameId);
        if (job && ["queued", "analyzing"].includes(job.status)) analysisRunner.stop();
      };
      signal?.addEventListener("abort", stopOwnedAnalysis, { once: true });
      void completion.finally(() => signal?.removeEventListener("abort", stopOwnedAnalysis)).catch(() => undefined);
      analyzingActionRef.current = action;
      setAnalyzingGameId(gameId);
      void completion;
      return;
    }
    if (!completion && isActiveSessionRequest(token, actionRequestRef)) {
      setIsSending(true);
      await finishBrowserAction(token, {
        ...nikolaBrowserResultInput(action, { gameId, failed: true }),
      });
    }
  };
  useEffect(() => {
    const token = actionRequestRef.current;
    if (!token || !analyzingGameId || !isActiveSessionRequest(token, actionRequestRef)) return;
    const job = jobs.find((candidate) => candidate.gameId === analyzingGameId);
    if (!job || job.status === "queued" || job.status === "analyzing") return;
    const action = analyzingActionRef.current;
    if (!action) return;
    analyzingActionRef.current = null;
    setAnalyzingGameId(null);
    setIsSending(true);
    void finishBrowserAction(
      token,
      nikolaBrowserResultInput(action, { gameId: analyzingGameId, failed: job.status !== "done" }),
    );
    // finishBrowserAction validates the captured action/session token.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- The captured request token validates this completion callback.
  }, [jobs, analyzingGameId]);

  const loadScope = `${querySessionId ?? "current"}:${queryGameId ?? ""}:${forceNewChat ? "new" : "resume"}:${handoffDraft ? location.key : ""}`;
  useEffect(() => {
    setSelectionContext(handoffFormat ? { format: handoffFormat, label: handoffFormat.toUpperCase() } : null);
  }, [loadScope, handoffFormat]);
  useEffect(() => {
    setRunFailure(null);
    if (sessionId && !forceNewChat && !querySessionId) {
      loadRequestRef.current = null;
      setIsResuming(false);
      void fetchMyPlayerProfile()
        .then((profile) => {
          setUsername(profile.username);
          setAccountSnapshot(profile);
          if (profile.id === useChatStore.getState().playerId) setVerifiedSessionId(sessionId);
        })
        .catch(() => undefined);
      return;
    }

    const token = { scope: loadScope, requestId: requestId() };
    loadRequestRef.current = token;
    const isCurrentLoad = () => isCurrentChatRequest(token, loadRequestRef.current, loadScope);
    setIsResuming(true);

    void (async () => {
      try {
        const profile = await fetchMyPlayerProfile();
        if (!isCurrentLoad()) return;
        setUsername(profile.username);
        setAccountSnapshot(profile);

        if (querySessionId) {
          setMessages([]);
          const current = await fetchSessionMessages(querySessionId);
          if (!isCurrentLoad()) return;
          if (current.sessionId === querySessionId) {
            setVerifiedSessionId(querySessionId);
            setSession(profile.id, querySessionId);
            setMessages(parseCurrentSessionMessages(current.messages));
          }
          return;
        }

        if (queryGameId) {
          let topic: string | undefined;
          if (reviewFactId) {
            try {
              topic = `Review · ${(await fetchReviewChatHandoff({ gameId: queryGameId, factId: reviewFactId })).summary}`;
            } catch {
              /* Topic evidence is optional; message context is revalidated before use. */
            }
          }
          if (!isCurrentLoad()) return;
          const created = await createCoachingSession(topic, queryGameId);
          if (!isCurrentLoad()) return;
          setMessages([]);
          setVerifiedSessionId(created.sessionId);
          setSession(profile.id, created.sessionId);
          return;
        }

        const current = forceNewChat ? { sessionId: null, messages: [] } : await fetchCurrentSession();
        if (!isCurrentLoad()) return;
        if (current.sessionId) {
          setVerifiedSessionId(current.sessionId);
          setSession(profile.id, current.sessionId);
          setMessages(parseCurrentSessionMessages(current.messages));
          return;
        }

        const created = await createCoachingSession();
        if (!isCurrentLoad()) return;
        setMessages([]);
        setVerifiedSessionId(created.sessionId);
        setSession(profile.id, created.sessionId);
      } catch (error) {
        if (!isCurrentLoad()) return;
        if (error instanceof ApiRequestError && error.status === 404) {
          setNeedsOnboarding(true);
          return;
        }
        reportError(error);
      } finally {
        if (isCurrentLoad()) setIsResuming(false);
      }
    })();

    return () => {
      if (isCurrentChatRequest(token, loadRequestRef.current, loadScope)) loadRequestRef.current = null;
    };
    // Session changes are results of this load, not new load requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Session changes are results of this scoped load, not new requests.
  }, [loadScope]);
  const handleSend = async (retry?: { message: string; index: number }, message = draft) => {
    const userMessage = retry?.message ?? message;
    if (!userMessage.trim()) return;
    if (!chatGate.allowed) {
      if (entitlementCta) void navigate(entitlementCta.href);
      return;
    }
    if (!sessionId || sessionId !== verifiedSessionId || isResuming || sendingRef.current) return;
    setRunFailure(null);
    resumeFollow();
    const token = { scope: sessionId, requestId: requestId() };
    recoveryRequestRef.current = null;
    actionKeysRef.current.clear();
    sendRequestRef.current = token;
    sendingRef.current = true;
    if (retry) updateMessage(retry.index, { failedDraft: undefined });
    appendMessage({ role: "user", content: userMessage });
    let workingParts: readonly NikolaMessagePart[] = [];
    appendMessage({ role: "assistant", content: NIKOLA_ANALYSIS_PLACEHOLDER, parts: workingParts });
    setIsSending(true);
    setCreditBlocked(false);

    try {
      const result = await executeNikolaRun(
        {
          sessionId,
          message: userMessage,
          requestId: token.requestId,
          ...buildChatRequestScope(reviewContext, contextDismissed, selectionContext),
        },
        (activity) => {
          if (!isActiveSessionRequest(token, sendRequestRef)) return;
          workingParts = mergeNikolaActivityParts(workingParts, activity);
          updateLastMessage({ content: NIKOLA_ANALYSIS_PLACEHOLDER, parts: workingParts });
        },
      );
      if (!isActiveSessionRequest(token, sendRequestRef)) return;
      terminalRunRef.current = result;
      if (result.status === "failed") {
        removeAnalysisPlaceholder();
        if (result.failureCode !== "cancelled") {
          if (result.retryable) setDraft(userMessage);
          reportNoAnswer(result.failureMessage ?? NIKOLA_FAILURE_REPLY, result.retryable ? userMessage : undefined);
        }
        await refreshCredits(token, sendRequestRef);
        return;
      }

      setDraft((current) => draftAfterAcceptedSend(current, userMessage));
      const draftKey = chatDraftKey(sessionId);
      if (localStorage.getItem(draftKey) === userMessage) localStorage.removeItem(draftKey);
      if (result.status === "complete") {
        updateLastMessage(chatResponseMessageWithProgress({ ...result, pendingAction: null }, workingParts));
        await refreshCredits(token, sendRequestRef);
        if (result.pendingAction)
          queueMicrotask(
            () => void handleResponseAction(result.pendingAction!, useChatStore.getState().messages.length - 1),
          );
        return;
      }

      updateLastMessage({
        role: "assistant",
        content: NIKOLA_ANALYSIS_PLACEHOLDER,
        parts: workingParts,
        pendingAction: result.pendingAction ?? null,
      });
      if (result.pendingAction)
        queueMicrotask(
          () => void handleResponseAction(result.pendingAction!, useChatStore.getState().messages.length - 1),
        );
      return;
    } catch (error) {
      if (!isActiveSessionRequest(token, sendRequestRef)) return;
      removeAnalysisPlaceholder();
      const retryable = !(error instanceof ApiRequestError && error.code === "QUOTA_EXCEEDED");
      if (retryable) setDraft(userMessage);
      reportNoAnswer(
        error instanceof ApiRequestError ? error.message : NIKOLA_FAILURE_REPLY,
        retryable ? userMessage : undefined,
      );
      if (error instanceof ApiRequestError && error.code === "QUOTA_EXCEEDED") setCreditBlocked(true);
    } finally {
      if (isActiveSessionRequest(token, sendRequestRef)) {
        sendingRef.current = false;
        setIsSending(false);
      }
    }
  };
  const handleResponseAction = async (action: PendingAction, messageIndex: number): Promise<void> => {
    if (!sessionId) return;
    const key =
      action.actionId ??
      `${sessionId}:${messageIndex}:${action.type}:${action.request?.requestId ?? action.gameId ?? action.fen ?? ""}`;
    if (actionKeysRef.current.has(key)) return;
    actionKeysRef.current.add(key);
    const token = { scope: sessionId, requestId: requestId() };
    actionRequestRef.current = token;
    let lease: NikolaBrowserLeaseHandle | null;
    try {
      lease = await acquireNikolaBrowserActionLease(action, nikolaRunSignal());
    } catch (error) {
      if (!isActiveSessionRequest(token, actionRequestRef)) return;
      actionKeysRef.current.delete(key);
      removeAnalysisPlaceholder();
      setIsSending(false);
      failNikolaRun(error);
      reportError(error);
      return;
    }
    if (!isActiveSessionRequest(token, actionRequestRef)) {
      lease?.stop();
      return;
    }
    if (!lease) {
      actionKeysRef.current.delete(key);
      setIsSending(false);
      void recoverNikolaRun(sessionId, (activity) => {
        if (isActiveSessionRequest(token, actionRequestRef)) mergeAnalysisPlaceholderActivity(activity);
      }).catch(() => undefined);
      return;
    }
    actionLeaseRef.current?.stop();
    actionLeaseRef.current = lease;
    actionRequestRef.current = token;
    lease.signal.addEventListener(
      "abort",
      () => {
        if (lease.signal.reason instanceof Error && lease.signal.reason.name === "AbortError") return;
        if (!isActiveSessionRequest(token, actionRequestRef)) return;
        actionRequestRef.current = null;
        setAnalyzingGameId(null);
        setEvaluatingLine(null);
        setIsSending(false);
        failNikolaRun(
          new ApiRequestError(
            "Nikola's analysis was interrupted. Reopen this conversation to continue.",
            "NIKOLA_LEASE_LOST",
            409,
          ),
        );
      },
      { once: true },
    );
    if (action.type === "start_game_analysis" && action.gameId) {
      await startAnalysisFromChat(token, action, messageIndex);
      return;
    }
    if (action.type === "browser_engine" && action.request) {
      await runBrowserEngine(token, action, messageIndex);
      return;
    }
    lease.stop();
    actionLeaseRef.current = null;
    updateMessage(messageIndex, { pendingAction: null });
    setIsSending(false);
    failNikolaRun(new Error("Nikola browser action was not supported."));
    reportNoAnswer();
  };

  useEffect(() => {
    if (
      !sessionId ||
      sessionId !== verifiedSessionId ||
      isResuming ||
      sendingRef.current ||
      nikolaRunState.phase === "running"
    )
      return;
    const continueResult = (result: SendMessageResult): void => {
      if (!result.pendingAction) return;
      const current = useChatStore.getState().messages;
      let index = -1;
      for (let candidate = current.length - 1; candidate >= 0; candidate -= 1) {
        if (current[candidate]?.pendingAction == null) continue;
        index = candidate;
        break;
      }
      if (index < 0) {
        appendMessage({
          role: "assistant",
          content: NIKOLA_ANALYSIS_PLACEHOLDER,
          parts: result.parts,
          pendingAction: result.pendingAction,
        });
        index = current.length;
      }
      setIsSending(true);
      void handleResponseAction(result.pendingAction, index);
    };
    if (nikolaRunState.phase === "waiting" && latestNikolaRunResult) {
      continueResult(latestNikolaRunResult);
      return;
    }
    if (nikolaRunState.phase === "idle") {
      const token = { scope: sessionId, requestId: requestId() };
      recoveryRequestRef.current = token;
      void recoverNikolaRun(sessionId, (activity) => {
        if (isActiveSessionRequest(token, recoveryRequestRef)) mergeAnalysisPlaceholderActivity(activity);
      }).catch(() => undefined);
      return;
    }
    if (
      terminalRunRef.current === nikolaRunState ||
      (latestNikolaRunResult && terminalRunRef.current === latestNikolaRunResult)
    )
      return;
    terminalRunRef.current = nikolaRunState;
    setIsSending(false);
    if (nikolaRunState.phase === "failed") {
      removeAnalysisPlaceholder();
      if (latestNikolaRunResult?.failureCode === "cancelled") return;
      if (latestNikolaRunResult?.failureMessage) {
        const previousQuestion = messages
          .slice()
          .reverse()
          .find(({ role }) => role === "user")?.content;
        if (latestNikolaRunResult.retryable && previousQuestion) setDraft(previousQuestion);
        reportNoAnswer(
          latestNikolaRunResult.failureMessage,
          latestNikolaRunResult.retryable ? previousQuestion : undefined,
        );
        return;
      }
      const previousQuestion = messages
        .slice()
        .reverse()
        .find(({ role }) => role === "user")?.content;
      if (previousQuestion) setDraft(previousQuestion);
      reportNoAnswer(
        nikolaRunError instanceof ApiRequestError ? nikolaRunError.message : NIKOLA_FAILURE_REPLY,
        previousQuestion,
      );
      return;
    }
    if (nikolaRunState.phase !== "completed") return;
    const token = { scope: sessionId, requestId: requestId() };
    recoveryRequestRef.current = token;
    void fetchSessionMessages(sessionId)
      .then((history) => {
        if (isActiveSessionRequest(token, recoveryRequestRef) && terminalRunRef.current !== latestNikolaRunResult)
          setMessages(parseCurrentSessionMessages(history.messages));
      })
      .catch((error: unknown) => {
        if (isActiveSessionRequest(token, recoveryRequestRef)) reportError(error);
      });
    // Shared executor owns the durable run; action identity prevents duplicate continuation.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Handler reads current store state and is intentionally not a dependency.
  }, [
    isResuming,
    latestNikolaRunResult,
    nikolaRunError,
    nikolaRunState,
    recoverNikolaRun,
    sessionId,
    verifiedSessionId,
  ]);

  if (needsOnboarding) return <Navigate to="/profile" replace />;
  if (isResuming) return <ChesslaLoadingMark fillContent page="Nikola" label="Opening your Nikola conversation" />;

  const handleChatAction = (action: NikolaAction): void => {
    if (action.type === "save_practice_set" && action.draftId) {
      void confirmPracticeSet(action.draftId)
        .then((result) => showNotification(`${result.title} saved · ${result.positionCount} positions`))
        .catch((caught: unknown) =>
          showNotification(
            caught instanceof ApiRequestError
              ? caught.message
              : "That practice set could not be saved. Please try again.",
            "error",
          ),
        );
      return;
    }
    if (action.type === "download_export" && action.exportId) {
      void downloadCoachExport(action.exportId).catch((caught: unknown) =>
        showNotification(
          caught instanceof ApiRequestError ? caught.message : "That download could not be prepared. Please try again.",
          "error",
        ),
      );
      return;
    }
    if (action.type === "open_game" && action.gameId) return void navigate(gameReviewUrl({ gameId: action.gameId }));
    if (action.type === "open_nikola_settings") return void navigate(nikolaSettingsUrl);
    if (action.type === "open_pattern" && action.patternId) {
      const segments = action.patternId.split(":");
      const embeddedCohort = segments[0] === "pattern" ? segments[1] : segments[0];
      const cohort =
        (["bullet", "blitz", "rapid", "daily"] as const).find((value) => value === embeddedCohort) ??
        reviewContext?.cohort;
      const motif =
        segments[0] === "pattern" && cohort
          ? segments.slice(2).join(":")
          : embeddedCohort === cohort
            ? segments.slice(1).join(":")
            : action.patternId;
      return void navigate(cohort ? patternWorkspaceUrl(cohort, motif) : "/patterns");
    }
    if (action.type === "start_game_analysis" && action.gameId) setAnalysisModalGameId(action.gameId);
    if (action.type === "evaluate_line" && action.fen)
      void handleSend(undefined, `Evaluate this position: ${action.fen}`);
  };

  return (
    <Box
      data-testid="nikola-chat"
      data-tour="chat-workspace"
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <ChatHeader balance={creditBalance} />
      <SingleGameAnalysisModal
        gameId={analysisModalGameId}
        onClose={() => setAnalysisModalGameId(null)}
        onComplete={({ gameId }) => {
          setAnalysisModalGameId(null);
          void navigate(gameReviewUrl({ gameId }));
        }}
      />
      <NikolaChatSurface
        variant="page"
        transcriptProps={transcriptProps}
        composerRef={composerRef}
        threadProps={{ component: "main", "data-tour": "chat-thread" }}
        afterThread={<NikolaActivityStatus label={activityStatusLabel} />}
        beforeComposer={
          newReply && (
            <Button
              onClick={resumeFollow}
              sx={{
                position: "absolute",
                right: { xs: 12, sm: 24 },
                bottom: "calc(100% + 8px)",
                zIndex: 4,
                borderRadius: 0,
                bgcolor: "secondary.main",
                color: "background.default",
                fontSize: ".65rem",
                fontWeight: 900,
              }}
            >
              NEW REPLY ↓
            </Button>
          )
        }
        composer={
          <Box sx={{ display: "grid", gap: 1 }}>
            {runFailure && (
              <Box
                role="alert"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  border: "1px solid",
                  borderColor: "error.main",
                  borderRadius: 1,
                }}
              >
                <Typography sx={{ color: "error.main", fontSize: ".75rem" }}>{runFailure.message}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  {runFailure.retryDraft && (
                    <Button size="small" onClick={() => void handleSend(undefined, runFailure.retryDraft)}>
                      Retry
                    </Button>
                  )}
                  <Button size="small" onClick={() => setRunFailure(null)}>
                    Dismiss
                  </Button>
                </Box>
              </Box>
            )}
            <ChatComposer
              key={loadScope}
              draft={draft}
              context={selectionContext}
              disabled={
                sessionId !== verifiedSessionId ||
                isResuming ||
                isSending ||
                runBusy ||
                !!analyzingGameId ||
                !!evaluatingLine ||
                !chatGate.allowed
              }
              running={isSending || runBusy || !!analyzingGameId || !!evaluatingLine}
              balance={creditBalance}
              estimatedCredits={estimatedCredits ?? chatGate.estimate}
              blocked={creditBlocked || !chatGate.allowed}
              blockedLabel={entitlementCta?.label}
              blockedHref={entitlementCta?.href}
              blockedDetail={entitlementCta?.detail}
              onChange={(value) => {
                draftTouchedRef.current = true;
                setDraft(value);
                setRunFailure(null);
                if (sessionId) localStorage.setItem(chatDraftKey(sessionId), value);
              }}
              onContextChange={setSelectionContext}
              onSend={() => void handleSend()}
              onCancel={() => {
                cancelActiveNikolaRun();
                if (analyzingGameId) analysisRunner.stop();
                sendRequestRef.current = null;
                actionRequestRef.current = null;
                sendingRef.current = false;
                setIsSending(false);
                setAnalyzingGameId(null);
                setEvaluatingLine(null);
                removeAnalysisPlaceholder();
              }}
            />
          </Box>
        }
      >
        <Box sx={{ minHeight: "100%" }}>
          {reviewContext && !contextDismissed && (
            <ChatContextStrip
              context={reviewContext}
              onDismiss={() => setContextDismissed(true)}
              onPrompt={(prompt) => {
                draftTouchedRef.current = true;
                setDraft(prompt);
                if (sessionId) localStorage.setItem(chatDraftKey(sessionId), prompt);
                requestAnimationFrame(() => document.getElementById("nikola-chat-input")?.focus());
              }}
            />
          )}
          {messages.map((message, index) => {
            const previous = messages[index - 1];
            const label =
              message.createdAt &&
              (!previous?.createdAt || chatDayLabel(previous.createdAt) !== chatDayLabel(message.createdAt))
                ? chatDayLabel(message.createdAt)
                : undefined;
            const placeholder = message.role === "assistant" && message.content === NIKOLA_ANALYSIS_PLACEHOLDER;
            return (
              <ChatTurn
                key={index}
                message={message}
                dayLabel={label}
                username={username}
                disabled={isSending || runBusy || !!analyzingGameId || !!evaluatingLine || !chatGate.allowed}
                estimatedCredits={estimatedCredits}
                placeholder={placeholder}
                onAction={handleChatAction}
                onRetry={
                  message.failedDraft ? () => void handleSend({ message: message.failedDraft!, index }) : undefined
                }
              >
                {analyzingGameId && message === messages[messages.length - 1] && (
                  <AnalysisProgressPanel gameId={analyzingGameId} dense />
                )}
                {evaluatingLine && message === messages[messages.length - 1] && (
                  <Typography sx={{ color: "text.secondary", fontSize: ".72rem" }}>
                    Analyzing {evaluatingLine.description}…
                  </Typography>
                )}
              </ChatTurn>
            );
          })}
          {isSending && messages.at(-1)?.role === "user" && (
            <ChatTurn
              message={{
                role: "assistant",
                content: "",
                parts: [],
              }}
              username={username}
              disabled
              estimatedCredits={estimatedCredits}
              placeholder
              onAction={() => undefined}
            />
          )}
          {messages.length === 0 && (
            <ChatWorkspaceIntro
              snapshot={accountSnapshot}
              context={selectionContext}
              onPrompt={(prompt) => {
                setDraft(prompt);
                if (sessionId) localStorage.setItem(chatDraftKey(sessionId), prompt);
                requestAnimationFrame(() => document.getElementById("nikola-chat-input")?.focus());
              }}
            />
          )}
        </Box>
      </NikolaChatSurface>
    </Box>
  );
};
